import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"

export type Activity = {
  jid: string
  general: string
  specific: string | undefined
}

type Jid = string

export type ActivityState = Record<Jid, Activity>

const initialState: ActivityState = {}

type UpsertActivityAction = {
  activity: Activity
}

type BatchUpsertActivitiesAction = {
  activities: Activity[]
}

export const activitySlice = createSlice({
  name: "activities",
  initialState,
  reducers: {
    upsertActivity: (state, action: PayloadAction<UpsertActivityAction>) => {
      state[action.payload.activity.jid] = action.payload.activity
    },
    batchUpsertActivities: (
      state,
      action: PayloadAction<BatchUpsertActivitiesAction>,
    ) => {
      for (const activity of action.payload.activities) {
        state[activity.jid] = activity
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const { upsertActivity, batchUpsertActivities } = activitySlice.actions

export const findActivity = (jid: string | undefined) => {
  return createSelector(
    (state: RootState) => state.activities,
    (activities): Activity | undefined => {
      if (!jid) return undefined

      return activities[jid]
    },
  )
}

export const selectActivities = (state: RootState) => {
  return state.activities
}

export const isDriverDriving = (jid: string | undefined) => {
  return createSelector(
    (state: RootState) => state.activities,
    (activities): boolean => {
      if (!jid) return false

      const activity = activities[jid]
      if (!activity) return false

      return activity.general === "driving"
    },
  )
}

export default activitySlice.reducer
